import {
    afterNextRender,
    DestroyRef,
    Directive,
    ElementRef,
    inject,
    signal,
} from '@angular/core';

@Directive({
    selector: '[qpxOverflow]',
    exportAs: 'qpxOverflow',
})
export class QpxOverflow {
    //#region Dependency injection
    private readonly destroyRef = inject(DestroyRef);
    private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
    //#endregion

    //#region State
    public readonly isOverflowing = signal(false);

    private resizeFrameId: number | null = null;
    //#endregion

    constructor() {
        afterNextRender({
            read: () => {
                const element = this.elementRef.nativeElement;

                this.setupResizeObserver(element);
                this.setupMutationObserver(element);
                this.scheduleMeasurement();
            },
        });

        this.destroyRef.onDestroy(() => {
            if (
                this.resizeFrameId !== null &&
                typeof cancelAnimationFrame === 'function'
            ) {
                cancelAnimationFrame(this.resizeFrameId);
            }
        });
    }

    //#region Public methods
    public remeasure(): void {
        this.measureOverflow();
    }
    //#endregion

    //#region Private methods
    private setupResizeObserver(element: HTMLElement): void {
        if (typeof ResizeObserver === 'undefined') {
            return;
        }

        const observer = new ResizeObserver(() => {
            this.scheduleMeasurement();
        });

        observer.observe(element);
        this.destroyRef.onDestroy(() => {
            observer.disconnect();
        });
    }

    private setupMutationObserver(element: HTMLElement): void {
        if (typeof MutationObserver === 'undefined') {
            return;
        }

        const observer = new MutationObserver(() => {
            this.scheduleMeasurement();
        });

        observer.observe(element, {
            attributes: true,
            attributeFilter: ['class', 'style'],
            childList: true,
            characterData: true,
            subtree: true,
        });

        this.destroyRef.onDestroy(() => {
            observer.disconnect();
        });
    }

    private scheduleMeasurement(): void {
        if (this.resizeFrameId !== null) {
            return;
        }

        if (typeof requestAnimationFrame !== 'function') {
            this.measureOverflow();
            return;
        }

        this.resizeFrameId = requestAnimationFrame(() => {
            this.resizeFrameId = null;
            this.measureOverflow();
        });
    }

    private measureOverflow(): void {
        const element = this.elementRef.nativeElement;
        const isOverflowing =
            element.scrollWidth > element.clientWidth ||
            element.scrollHeight > element.clientHeight;

        if (this.isOverflowing() !== isOverflowing) {
            this.isOverflowing.set(isOverflowing);
        }
    }
    //#endregion
}
